
import React, { useState, useEffect } from 'react';
import { Lead } from '@/types/lead';
import { PlaceReview, getPlaceDetails, getPlaceIdFromLead } from '@/services/reviewsService';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, Star, User, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDateYMD } from '@/utils/dateFormat';

interface ReviewsPanelProps {
  lead: Lead;
}

const ReviewsPanel: React.FC<ReviewsPanelProps> = ({ lead }) => {
  const [reviews, setReviews] = useState<PlaceReview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [averageRating, setAverageRating] = useState<number | null>(null);
  const [totalReviews, setTotalReviews] = useState<number | null>(null);

  const fetchReviews = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // First, try to get the place ID from the lead
      const placeId = lead.placeId || getPlaceIdFromLead(lead);
      
      if (!placeId) {
        setError("No Google Place ID found for this business");
        setIsLoading(false);
        return;
      }
      
      const placeDetails = await getPlaceDetails(placeId);
      
      if (!placeDetails || !placeDetails.result) {
        setError("Could not fetch reviews for this business");
        setIsLoading(false);
        return;
      }
      
      setReviews(placeDetails.result.reviews || []);
      setAverageRating(placeDetails.result.rating || null);
      setTotalReviews(placeDetails.result.user_ratings_total || null);
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setError("An error occurred while fetching reviews");
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchReviews();
  }, [lead]);
  
  // Format the timestamp to a readable date
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
    return formatDateYMD(date);
  };
  
  // Generate star rating display
  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            className={`h-4 w-4 ${i < rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} 
          />
        ))}
      </div>
    );
  };

  const handleRefreshReviews = () => {
    fetchReviews();
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-center items-center h-40">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Loading reviews...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>Google Reviews</CardTitle>
          <CardDescription className="flex items-center gap-2">
            {averageRating !== null && (
              <>
                <span className="font-medium text-lg">{averageRating.toFixed(1)}</span>
                {renderStars(averageRating)}
                <span className="text-muted-foreground">({totalReviews} reviews)</span>
              </>
            )}
          </CardDescription>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleRefreshReviews}
          disabled={isLoading}
          className="ml-auto"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Update Reviews
        </Button>
      </CardHeader>
      <CardContent>
        {error ? (
          <div className="p-4 text-center">
            <p className="text-muted-foreground">{error}</p>
            <p className="mt-2 text-sm">This may happen if the business doesn't have a Google Places ID or if there was an error retrieving the reviews.</p>
          </div>
        ) : reviews.length > 0 ? (
          <div className="space-y-4">
            {reviews.map((review, index) => (
              <div key={index} className="border rounded-lg p-4 bg-card">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-10 w-10 rounded-full overflow-hidden bg-muted">
                    {review.profile_photo_url ? (
                      <img 
                        src={review.profile_photo_url} 
                        alt={review.author_name} 
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center">
                        <User className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="font-medium">{review.author_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {review.relative_time_description || formatDate(review.time)}
                    </div>
                  </div>
                  <div className="ml-auto">
                    {renderStars(review.rating)}
                  </div>
                </div>
                <p className="text-sm whitespace-pre-line">{review.text}</p>
                {review.language && review.language !== 'en' && (
                  <Badge variant="outline" className="mt-2">
                    {review.language.toUpperCase()}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-muted-foreground p-4">
            No reviews available for this business.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default ReviewsPanel;
